import {
  faChevronDown,
  faHome,
  faLandmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import styled from 'styled-components';
import Spacer from '../Spacer';
import AccountDropdown from './AccountDropdown';
import AccountOptionsDropdown from './AccountOptionsDropdown';
import { AccountSelector_account } from './__generated__/AccountSelector_account.graphql';
import { AccountSelector_accounts } from './__generated__/AccountSelector_accounts.graphql';
import { useTransition, animated } from 'react-spring';
import useMeasure from 'react-use-measure';

const Container = styled.div`
  position: relative;
`;

const Dropdown = styled.div`
  box-shadow: 0 0 20px #00000021;
  padding: 10px 0px;
  margin-top: 6px;
  border-radius: 6px;
  width: 100%;
  background: white;
  position: absolute;
  z-index: 11;
  overflow: hidden;
`;

const DropdownContent = styled.div<{ height: string }>`
  position: relative;
  height: ${(props) => props.height};
`;

const BackgroundHider = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  z-index: 10;
  top: 0;
  left: 0;
`;

const CurrentItem = styled.div<{ dropped: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 21px;
  border-radius: 30px;
  cursor: pointer;
  color: #171c3c;
  transition: all 0.3s;
  ${(props) =>
    props.dropped
      ? `
      box-shadow: 0 0 20px #00000012;
      background: #dcdcdc14;
  `
      : `
    &:hover {
      background: #ffffff12;
    }
  `}
`;

const Content = styled.div`
  display: flex;
  align-items: center;
`;

const AccountName = styled.div`
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #dee1ff;
`;

const DropdownButton = styled.div``;

type Props = {
  children?: React.ReactNode;
  account?: AccountSelector_account;
  subheading: string;
  accounts: AccountSelector_accounts;
  className?: string;
};

function AccountSelector({
  account,
  subheading,
  children,
  accounts,
  className,
}: Props) {
  const [dropped, setDropped] = useState(false);

  const [items, setItems] = useState<'main' | 'accounts' | undefined>('main');

  const [mainRef, mainDim] = useMeasure();
  const [accountsRef, accountsDim] = useMeasure();

  const transitions = useTransition(items, {
    initial: { opacity: 1, x: 0 },
    from: { opacity: 0, x: 400 },
    enter: { opacity: 1, x: 0 },
    leave: { opacity: 0, x: -400 },
    delay: 0,
  });

  return (
    <Container className={className}>
      {dropped && <BackgroundHider onClick={() => setDropped(false)} />}
      <CurrentItem
        onClick={() => {
          setDropped(!dropped);
          setItems('main');
        }}
        dropped={dropped}
      >
        <Content>
          <FontAwesomeIcon
            icon={account?.userMembership.isDefault ? faHome : faLandmark}
            color='#DEE1FF'
          />
          <Spacer horizontal spacing={2} />
          <AccountName>{account?.name}</AccountName>
        </Content>
        <DropdownButton>
          <FontAwesomeIcon icon={faChevronDown} color='#DEE1FF' />
        </DropdownButton>
      </CurrentItem>

      {dropped && (
        <Dropdown>
          <DropdownContent
            height={`${
              items === 'main' ? mainDim.height : accountsDim.height
            }px`}
          >
            {transitions(({ opacity, x }, item) =>
              item === 'main' ? (
                <animated.div
                  style={{
                    opacity: opacity.to({ range: [0.0, 1.0], output: [0, 1] }),
                    position: 'absolute',
                    width: '100%',
                    transform: x.to(
                      (i) =>
                        `translate(${items === 'accounts' ? i : -i}px, 0px)`
                    ),
                  }}
                >
                  <AccountOptionsDropdown
                    ref={mainRef}
                    account={account}
                    onClose={() => {
                      setDropped(false);
                    }}
                    onAccountsClick={() => {
                      setItems('accounts');
                    }}
                  />
                </animated.div>
              ) : (
                <animated.div
                  style={{
                    opacity: opacity.to({ range: [0.0, 1.0], output: [0, 1] }),
                    position: 'absolute',
                    width: '100%',
                    transform: x.to(
                      (i) =>
                        `translate(${items === 'accounts' ? i : -i}px, 0px)`
                    ),
                  }}
                >
                  <AccountDropdown
                    ref={accountsRef}
                    accounts={accounts}
                    onClose={() => setDropped(false)}
                    onBack={() => setItems('main')}
                  />
                </animated.div>
              )
            )}
          </DropdownContent>
        </Dropdown>
      )}
    </Container>
  );
}

export default createFragmentContainer(AccountSelector, {
  accounts: graphql`
    fragment AccountSelector_accounts on Account @relay(plural: true) {
      ...AccountDropdown_accounts
    }
  `,
  account: graphql`
    fragment AccountSelector_account on Account {
      name
      userMembership {
        isDefault
      }
      ...AccountOptionsDropdown_account
    }
  `,
});
